import { useState, useEffect, useCallback } from 'react';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import UsageInfo from '../../components/UsageInfo';
import { getGunlukDoluluk } from '../../services/api';
import YilDropdown from '../../components/YilDropdown';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import { useKasa } from '../../context/KasaContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import DataTable, { Rozet, OranCubugu } from '../../components/DataTable';

const TUM_AYLAR   = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

function fmt(n, para = 'TL') {
  if (!isFinite(n) || isNaN(n)) return '—';
  return Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ' + para;
}

function StatCard({ icon, label, value, sub, bgClass, textClass, loading }) {
  return (
    <div className={`relative overflow-hidden rounded-[2.5rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group ${bgClass} border border-white/40 shadow-lg flex flex-col justify-between transform-gpu`}>
      <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500 will-change-transform">{icon}</div>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-[10px] font-black uppercase tracking-[0.2em] sm:truncate w-3/4 ${textClass}`}>{label}</span>
        <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white/80 backdrop-blur-md shadow-sm font-emoji flex-shrink-0">{icon}</span>
      </div>
      <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none">{loading ? '…' : value}</div>
      <p className={`text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 truncate ${textClass}`}>{sub}</p>
    </div>
  );
}

export default function GunlukOrtalama() {
  const { paraBirimleri } = useKasa();
  const now = new Date();
  const [yil,     setYil]     = useState(now.getFullYear());
  const [ay,      setAy]      = useState(now.getMonth()); // 0-indexed
  const [para,    setPara]    = useState('Hepsi');
  const [detayId, setDetayId] = useState(null);
  const [acikGun, setAcikGun] = useState(null);

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Tablo hücrelerinde tekrar eden gün hesapları
  const HAFTA = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
  const haftaGunuAdi = (gun) => HAFTA[new Date(yil, ay, gun).getDay()];
  const haftaSonuMu  = (gun) => { const g = haftaGunuAdi(gun); return g === 'Cmt' || g === 'Paz'; };
  const bugunMu      = (gun) => {
    const b = new Date();
    return b.getDate() === gun && b.getMonth() === ay && b.getFullYear() === yil;
  };

  const fetchData = useCallback(async (y, a, p) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getGunlukDoluluk(y, a + 1, p);
      setData(res);
    } catch {
      setError('Veriler yüklenemedi.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(yil, ay, para); }, [yil, ay, para, fetchData]);

  const gunler    = data?.satirlar ?? [];
  const toplamOda = data?.toplamOda ?? 0;

  const toplamDolu  = gunler.reduce((s, g) => s + g.doluOda, 0);
  const toplamGelir = gunler.reduce((s, g) => s + g.toplamGelir, 0);
  const doluGunlar  = gunler.filter(g => g.doluOda > 0);
  const toplamOrt   = doluGunlar.length > 0
    ? toplamGelir / doluGunlar.reduce((s, g) => s + g.doluOda, 0)
    : 0;
  const ortDoluluk = gunler.length > 0 && toplamOda > 0
    ? Math.round((toplamDolu / (gunler.length * toplamOda)) * 100)
    : 0;
  const toplamRevPar = gunler.length > 0
    ? gunler.reduce((s, g) => s + g.revPar, 0) / gunler.length
    : 0;
  const paraBirimi = para === 'Hepsi' ? 'TL' : para;

  function excelExport() {
    const baslik = ['Tarih','Gün','Dolu Oda','Boş Oda','Doluluk %','Toplam Gelir','Ort. Oda Fiyatı','RevPAR'];
    const satirlar = gunler.map(g => {
      const haftaGunu = ['Paz','Pzt','Sal','Çar','Per','Cum','Cmt'][new Date(yil, ay, g.gun).getDay()];
      return [g.tarih, haftaGunu, g.doluOda, g.bosOda, g.doluluk + '%', g.toplamGelir, g.ortalamaOdaFiyati, g.revPar];
    });
    downloadExcel(`gunluk-doluluk-${yil}-${String(ay+1).padStart(2,'0')}`, 'Günlük Analiz', baslik, satirlar);
  }

  function yazdir() {
    const TUM_AYLAR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
    const baslik = ['Tarih','Gün','Dolu Oda','Boş Oda','Doluluk %','Ort. Oda Fiyatı','RevPAR'];
    const satirlar = gunler.map(g => {
      const haftaGunu = ['Paz','Pzt','Sal','Çar','Per','Cum','Cmt'][new Date(yil, ay, g.gun).getDay()];
      return [g.tarih, haftaGunu, g.doluOda, g.bosOda, g.doluluk + '%', g.ortalamaOdaFiyati, g.revPar];
    });
    printTable('Günlük Ortalama Analizi', baslik, satirlar, TUM_AYLAR[ay] + ' ' + yil + ' — ' + gunler.length + ' gün');
  }

  return (
    <div className="p-3 md:p-6 space-y-8 min-h-screen bg-gray-50/50 transform-gpu animate-fade-in">

      {/* Premium Header */}
      <div className="relative overflow-hidden bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-xl p-4 md:p-8 transform-gpu">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-indigo-100 rounded-full blur-[80px] opacity-50 will-change-transform"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-purple-100 rounded-full blur-[80px] opacity-40 will-change-transform"></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-6 min-w-0">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-700 rounded-2xl flex-shrink-0 flex items-center justify-center text-white text-3xl shadow-2xl shadow-indigo-200 transform-gpu hover:scale-105 transition-transform duration-500">📊</div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter truncate">Günlük Ortalama Analizi</h1>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest truncate">{TUM_AYLAR[ay]} {yil} — Aylık doluluk ve fiyat dökümü</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <button onClick={excelExport} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white/80 backdrop-blur-md border-2 border-emerald-100/50 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-emerald-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
              <span>📊</span> EXCEL'E AKTAR
            </button>
            <button onClick={yazdir} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white/80 backdrop-blur-md border-2 border-gray-100/50 hover:border-gray-800 hover:bg-gray-800 hover:text-white text-gray-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-gray-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
              <span>🖨️</span> YAZDIR
            </button>
          </div>
        </div>
      </div>

      {/* Hata */}
      {error && (
        <div className="bg-red-50/80 backdrop-blur-md border border-red-200/50 text-red-700 rounded-2xl px-6 py-4 text-sm font-bold shadow-lg shadow-red-50/50 flex items-center gap-3 animate-shake transform-gpu">
          <span className="text-xl">⚠️</span> {error}
        </div>
      )}

      {/* Akıllı Filtre Paneli */}
      <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl p-6 md:p-8 z-20 relative transform-gpu">
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
           <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse shadow-sm" /> Dönem Seçimi & İzleme Paneli
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-3">
             <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Yıl Gösterimi</label>
             <YilDropdown value={yil} onChange={setYil} className="w-full bg-gray-50/50 border-2 border-transparent focus:bg-white focus:border-indigo-400 rounded-2xl px-4 py-3 font-black text-sm text-gray-700 outline-none cursor-pointer transition-all" />
          </div>
          <div className="md:col-span-4">
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Ay Seçimi</label>
            <div className="relative group">
              <select value={ay} onChange={e => setAy(Number(e.target.value))} className="w-full bg-gray-50/50 border-2 border-transparent group-hover:border-indigo-300 focus:bg-white focus:border-indigo-400 rounded-2xl px-4 py-3 font-black text-sm text-gray-700 outline-none cursor-pointer appearance-none transition-all">
                {TUM_AYLAR.map((a, i) => <option key={a} value={i}>{a}</option>)}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-indigo-500">▼</span>
            </div>
          </div>
          <div className="md:col-span-5">
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Para Birimi Kontrolü</label>
            <div className="relative group">
              <select value={para} onChange={e => setPara(e.target.value)} className="w-full bg-gray-50/50 border-2 border-transparent group-hover:border-indigo-300 focus:bg-white focus:border-indigo-400 rounded-2xl px-4 py-3 font-black text-sm text-gray-700 outline-none cursor-pointer appearance-none transition-all">
                <option value="Hepsi">Tüm Kurları Dahil Et (Brüt İzleme)</option>
                <option disabled>──────</option>
                {(paraBirimleri || []).map(p => {
                  const kod = p.tcmbKod || p.ad;
                  return <option key={kod} value={kod}>{kod}</option>;
                })}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-indigo-500">▼</span>
            </div>
          </div>
        </div>
      </div>

      {/* Lüks Özet Kartlar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-5 relative z-10 transform-gpu">
        <StatCard icon="🏨" label="Oda Kapasitesi" value={`${toplamOda} oda`} sub={`${gunler.length} gün × ${toplamOda}`} bgClass="bg-gradient-to-br from-slate-50/50 to-gray-50/50" textClass="text-slate-600" loading={loading} />
        <StatCard icon="📈" label="Ort. Doluluk" value={`%${ortDoluluk}`} sub={`${toplamDolu} oda-gece dolu`} bgClass={ortDoluluk>=70?'bg-gradient-to-br from-emerald-50/50 to-green-50/50':ortDoluluk>=40?'bg-gradient-to-br from-amber-50/50 to-orange-50/50':'bg-gradient-to-br from-red-50/50 to-rose-50/50'} textClass={ortDoluluk>=70?'text-emerald-600':ortDoluluk>=40?'text-amber-600':'text-red-500'} loading={loading} />
        <StatCard icon="💰" label="Aylık Ciro" value={fmt(toplamGelir, paraBirimi)} sub={`${TUM_AYLAR[ay]} Net Gelir`} bgClass="bg-gradient-to-br from-blue-50/50 to-indigo-50/50" textClass="text-blue-600" loading={loading} />
        <StatCard icon="📊" label="ADR (Birim Fiyat)" value={fmt(toplamOrt, paraBirimi)} sub="Dolu oda başı ort." bgClass="bg-gradient-to-br from-purple-50/50 to-fuchsia-50/50" textClass="text-purple-600" loading={loading} />
      </div>

      {/* Tablo – Mobil Kartlar */}
      <div className="md:hidden space-y-3 transform-gpu">
        {loading ? (
          <LoadingSpinner fullPage text="Performans Hazırlanıyor" color="indigo" />
        ) : gunler.length === 0 ? (
          <EmptyState icon="📭" text="Dönem kaydı bulunamadı" />
        ) : gunler.map((g) => {
          const haftaGunu = ['Paz','Pzt','Sal','Çar','Per','Cum','Cmt'][new Date(yil, ay, g.gun).getDay()];
          const isWeekend = haftaGunu === 'Cmt' || haftaGunu === 'Paz';
          const isToday = new Date().getDate() === g.gun && new Date().getMonth() === ay && new Date().getFullYear() === yil;
          const acik = acikGun === g.gun;
          return (
            <div key={g.gun} className={`bg-white/80 backdrop-blur-xl rounded-[2rem] border-2 shadow-xl p-5 transition-all transform-gpu ${isToday ? 'border-indigo-400 ring-4 ring-indigo-50' : 'border-white/40'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl flex flex-col items-center justify-center border shadow-sm ${isWeekend ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                     <span className={`text-[8px] font-black uppercase tracking-widest ${isWeekend ? 'text-amber-500' : 'text-gray-400'}`}>{haftaGunu}</span>
                     <span className="text-xl font-black text-gray-900 leading-none">{g.gun}</span>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{g.tarih}</p>
                    <div className="flex items-center gap-2">
                       <span className={`text-[10px] font-black px-2 py-0.5 rounded-lg ${isWeekend ? 'bg-amber-100/50 text-amber-600' : 'bg-gray-100/50 text-gray-500'}`}>{isWeekend ? 'Haftasonu' : 'Haftaiçi'}</span>
                       {isToday && <span className="text-[8px] bg-indigo-600 text-white px-2 py-0.5 rounded-lg font-black uppercase tracking-widest animate-pulse">Bugün</span>}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                   <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Doluluk</p>
                   <p className={`text-lg font-black leading-none ${g.doluluk>=70?'text-emerald-600':g.doluluk>=40?'text-amber-600':'text-red-500'}`}>%{g.doluluk}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs bg-gray-50/50 p-4 rounded-2xl mb-4 border border-white/50 shadow-inner">
                 <div className="space-y-1">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Dolu / Boş</p>
                    <p className="font-black text-gray-800">{g.doluOda} Dolu <span className="text-gray-300 mx-1">/</span> {g.bosOda} Boş</p>
                 </div>
                 <div className="space-y-1">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Net Gelir</p>
                    <p className="font-black text-indigo-600 truncate">{g.toplamGelir > 0 ? fmt(g.toplamGelir, paraBirimi) : '—'}</p>
                 </div>
                 <div className="space-y-1">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Gecelik ADR</p>
                    <p className="font-black text-purple-600">{g.doluOda > 0 ? fmt(g.ortalamaOdaFiyati, paraBirimi) : '—'}</p>
                 </div>
                 <div className="space-y-1">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">RevPAR</p>
                    <p className="font-black text-gray-700">{g.revPar > 0 ? fmt(g.revPar, paraBirimi) : '—'}</p>
                 </div>
              </div>
              {g.rezervasyonlar?.length > 0 && (
                <div>
                  <button onClick={() => setAcikGun(acik ? null : g.gun)}
                    className={`w-full text-[10px] font-black uppercase tracking-widest py-3 rounded-xl transition-all shadow-lg active:scale-95 ${acik ? 'bg-indigo-700 text-white shadow-indigo-100' : 'bg-white border-2 border-indigo-100 text-indigo-600 shadow-slate-100'}`}>
                    {g.rezervasyonlar.length} Rezervasyon Detayı {acik ? '▲' : '▼'}
                  </button>
                  {acik && (
                    <div className="mt-3 space-y-2 animate-fade-in transform-gpu">
                      {g.rezervasyonlar.map(r => (
                        <div key={r.rezervasyonId} className="bg-white/60 backdrop-blur-md rounded-2xl border border-white p-3 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-black text-[10px] text-gray-400 tracking-widest">#{r.rezNo}</span>
                            <span className="bg-indigo-50 text-indigo-600 text-[10px] font-black px-2 py-0.5 rounded-lg border border-indigo-100">Oda {r.odaNo}</span>
                          </div>
                          <p className="font-black text-gray-900 text-xs truncate mb-2">{r.misafirAdlari || 'İsimsiz'}</p>
                          <div className="flex items-center justify-between gap-2 border-t border-gray-50 pt-2">
                            <span className="text-[9px] font-bold text-gray-400">{r.girisTarihi} - {r.cikisTarihi}</span>
                            <div className="flex items-center gap-2">
                               <span className="font-black text-indigo-600 text-[10px]">{fmt(r.gunlukFiyat, r.paraBirimi)}</span>
                               <button onClick={() => setDetayId(r.rezervasyonId)} className="w-6 h-6 bg-indigo-500 text-white rounded-lg flex items-center justify-center text-xs shadow-sm shadow-indigo-100 font-bold hover:scale-110 active:scale-90 transition-transform">›</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tablo – Masaüstü */}
      <div className="hidden md:block bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl overflow-hidden relative z-10 transform-gpu">
        <div className="px-10 py-6 border-b border-gray-100/50 bg-gray-50/30 flex items-center justify-between">
          <div>
            <p className="text-sm font-black text-gray-900 uppercase tracking-tighter">Aylık Performans Grid Sistemi</p>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
              <span className="text-indigo-600 font-black">{gunler.length}</span> günlük veri seti analiz edilmiştir.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest">
               <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shadow-sm" /> Haftasonu
            </div>
            <div className="flex items-center gap-1.5 text-[9px] font-black text-indigo-500 uppercase tracking-widest">
               <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 shadow-sm" /> Bugün
            </div>
          </div>
        </div>

        <div className="overflow-x-auto min-h-[400px]">
          {loading ? (
            <LoadingSpinner fullPage text="Veri Haritaları Hesaplanıyor" color="indigo" />
          ) : (
            <DataTable
              anahtarAlan="gun"
              satirlar={gunler}
              bosIkon="📁"
              bosMetin="Bu doneme ait analiz kaydi yok"
              toplamGoster
              toplamEtiketi="PERFORMANS OZETI"
              satirSinifi={g => bugunMu(g.gun) ? 'bg-indigo-50/60' : (haftaSonuMu(g.gun) ? 'bg-amber-50/30' : '')}
              detaySatiri={g => (acikGun === g.gun && g.rezervasyonlar?.length > 0) ? (
                <>
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.25em] mb-5 flex items-center gap-3">
                    <span className="w-1.5 h-5 bg-indigo-500 rounded-full" /> {g.tarih} tarihli aktif konaklamalar
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {g.rezervasyonlar.map(r => (
                      <div key={r.rezervasyonId} className="flex items-center gap-5 bg-white border-2 border-transparent hover:border-indigo-100 rounded-2xl p-4 transition-all shadow-sm">
                        <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex flex-col items-center justify-center flex-shrink-0">
                          <span className="text-[8px] font-black text-indigo-300 uppercase leading-none mb-1">ODA</span>
                          <span className="text-lg font-black text-indigo-700 leading-none tabular-nums">{r.odaNo}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">#{r.rezNo}</p>
                          <p className="font-black text-gray-900 text-sm truncate">{r.misafirAdlari || 'Isimsiz'}</p>
                          <p className="text-[10px] font-bold text-gray-400 mt-1">
                            {r.girisTarihi} - {r.cikisTarihi}
                            <span className="mx-2 text-gray-200">|</span>
                            <span className="text-purple-500">{fmt(r.gunlukFiyat, r.paraBirimi)}/gece</span>
                          </p>
                        </div>
                        <button onClick={() => setDetayId(r.rezervasyonId)}
                          className="w-12 h-12 bg-white border border-gray-100 hover:bg-indigo-600 hover:text-white rounded-2xl flex items-center justify-center transition-all shadow-sm active:scale-90 flex-shrink-0 text-xl font-bold">
                          &rsaquo;
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              kolonlar={[
                {
                  anahtar: 'tarih', baslik: 'Tarih', siralanir: true, filtrelenir: true,
                  ciz: g => (
                    <div className="flex items-center gap-3 whitespace-nowrap">
                      <div className={'w-8 h-8 rounded-xl flex items-center justify-center font-black text-[11px] shadow-sm ' +
                        (bugunMu(g.gun) ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-100 text-gray-700')}>{g.gun}</div>
                      <span className={bugunMu(g.gun) ? 'font-black text-indigo-700' : ''}>{g.tarih}</span>
                    </div>
                  ),
                  toplam: () => 'PERFORMANS OZETI',
                },
                {
                  anahtar: 'gunAdi', baslik: 'Gun', hizala: 'orta',
                  deger: g => haftaGunuAdi(g.gun),
                  ciz: g => <Rozet renk={haftaSonuMu(g.gun) ? 'amber' : 'gray'}>{haftaGunuAdi(g.gun)}</Rozet>,
                  toplam: () => '',
                },
                {
                  anahtar: 'doluOda', baslik: 'Dolu oda', hizala: 'orta', siralanir: true,
                  ciz: g => <span className={'font-black ' + (g.doluOda > 0 ? 'text-emerald-600' : 'text-gray-300')}>{g.doluOda}</span>,
                  toplam: () => toplamDolu,
                },
                {
                  anahtar: 'bosOda', baslik: 'Bos', hizala: 'orta', siralanir: true,
                  ciz: g => <span className="text-gray-400 font-bold">{g.bosOda}</span>,
                  toplam: () => '',
                },
                {
                  anahtar: 'doluluk', baslik: 'Doluluk %', hizala: 'sag', siralanir: true,
                  ciz: g => <OranCubugu oran={g.doluluk} etiket={'%' + g.doluluk}
                              renk={g.doluluk >= 70 ? 'emerald' : g.doluluk >= 40 ? 'amber' : 'rose'} />,
                  toplam: () => '%' + ortDoluluk,
                },
                {
                  anahtar: 'toplamGelir', baslik: 'Toplam gelir', hizala: 'sag', siralanir: true,
                  ciz: g => g.toplamGelir > 0
                    ? <span className="font-black text-gray-900 tabular-nums">{fmt(g.toplamGelir, paraBirimi)}</span>
                    : <span className="text-gray-200">&mdash;</span>,
                  toplam: () => fmt(toplamGelir, paraBirimi),
                },
                {
                  anahtar: 'ortalamaOdaFiyati', baslik: 'ADR (birim)', hizala: 'sag', siralanir: true,
                  ciz: g => g.doluOda > 0
                    ? <span className="font-black text-purple-700 tabular-nums">{fmt(g.ortalamaOdaFiyati, paraBirimi)}</span>
                    : <span className="text-gray-200">&mdash;</span>,
                  toplam: () => fmt(toplamOrt, paraBirimi),
                },
                {
                  anahtar: 'revPar', baslik: 'RevPAR', hizala: 'sag', siralanir: true,
                  ciz: g => g.revPar > 0
                    ? <span className="font-bold text-gray-600 tabular-nums">{fmt(g.revPar, paraBirimi)}</span>
                    : <span className="text-gray-200">&mdash;</span>,
                  toplam: () => fmt(toplamRevPar, paraBirimi),
                },
                {
                  anahtar: 'aksiyon', baslik: 'Aksiyon', hizala: 'orta',
                  ciz: g => g.rezervasyonlar?.length > 0 ? (
                    <button onClick={e => { e.stopPropagation(); setAcikGun(acikGun === g.gun ? null : g.gun); }}
                      className={'text-[10px] font-black px-4 py-2 rounded-xl transition-all uppercase tracking-widest active:scale-95 whitespace-nowrap ' +
                        (acikGun === g.gun ? 'bg-indigo-700 text-white' : 'bg-white border-2 border-gray-100 text-indigo-600 hover:border-indigo-300')}>
                      {g.rezervasyonlar.length} Rez. {acikGun === g.gun ? '\u25B2' : '\u25BC'}
                    </button>
                  ) : <span className="text-gray-200">&mdash;</span>,
                  toplam: () => TUM_AYLAR[ay],
                },
              ]}
            />
          )}
        </div>
      </div>

      {detayId && (
        <RezervasyonDetayModal rezervasyonId={detayId} onClose={() => setDetayId(null)} onGuncelle={() => { setDetayId(null); fetchData(yil, ay, para); }} />
      )}

      <UsageInfo 
        color="indigo" 
        description="Günlük Ortalama sayfası, otelinizin belirli bir aydaki performansına (Geceleme, ADR, RevPAR) dair kuşbakışı bir veri seti sunar. İşletmenizin finansal ritmini takip etmek için optimize edilmiştir." 
        scenarios={[
          { title: "ADR Analizi (Average Daily Rate)", content: "'Ort. Oda Fiyatı' sütunu, o gün satılan odaların ağırlıklı ortalamasıdır. ADR değerinin haftasonlarında hafta içinden daha yüksek veya eşit olması idealdir. Bu veriyi fiyatlama stratejinizde dinamik olarak kullanın." },
          { title: "RevPAR Analizi (Revenue per Available Room)", content: "RevPAR, otelin toplam gelirini toplam oda kapasitesine böler. Hem doluluğu hem de fiyatı içine alan en sağlıklı başarı göstergesidir. Yıl içi kıyaslamalarda RevPAR artışını hedeflemelisiniz." },
          { title: "Detay İnceleme (Geçmiş Gün Akışı)", content: "Satır sonundaki 'Rezervasyon' butonuna basarak, o gecede otelde konaklayan misafirlerin kırımını görebilirsiniz. Listede gördüğünüz oda bazlı fiyatlar, ADR değerini oluşturan alt kalemleri net bir şekilde anlamanızı sağlar." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s cubic-bezier(.22,1,.36,1) forwards; }
        .animate-shake { animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes shake { 10%, 90% { transform: translate3d(-1px, 0, 0); } 20%, 80% { transform: translate3d(2px, 0, 0); } 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); } 40%, 60% { transform: translate3d(4px, 0, 0); } }
      `}</style>
    </div>
  );
}
